import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form | check-email | done
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    setError('');

    // 1. Create auth user
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName.trim() } },
    });

    if (signUpError) {
      setError(signUpError.message);
      setLoading(false);
      return;
    }

    const user = signUpData.user;
    const session = signUpData.session;

    if (!session) {
      // Email confirmation required — store org name and prompt user
      sessionStorage.setItem('pending_org_name', orgName.trim());
      setStep('check-email');
      setLoading(false);
      return;
    }

    // 2. Session available immediately — create org and membership
    const { data: org, error: orgError } = await supabase
      .from('organisations')
      .insert({ name: orgName.trim() })
      .select('id')
      .single();

    if (orgError) {
      setError(`Failed to create organisation: ${orgError.message}`);
      setLoading(false);
      return;
    }

    const { error: memberError } = await supabase
      .from('organisation_members')
      .insert({ org_id: org.id, user_id: user.id, role: 'owner' });

    if (memberError) {
      setError(`Failed to set up membership: ${memberError.message}`);
      setLoading(false);
      return;
    }

    navigate('/dashboard', { replace: true });
  };

  if (step === 'check-email') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-sm w-full text-center border border-gray-700">
          <div className="text-4xl mb-4">📬</div>
          <h2 className="text-white text-xl font-bold mb-2">Check your email</h2>
          <p className="text-gray-400 text-sm mb-4">
            We sent a confirmation link to <span className="text-white">{email}</span>.
            After confirming, sign in and you'll be prompted to finish setting up your organisation.
          </p>
          <Link
            to="/login"
            className="block w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl transition-colors"
          >
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-orange-500">GroundCheck</h1>
          <p className="text-gray-400 mt-2 text-sm">Create your organisation account</p>
        </div>

        <div className="bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-700">
          {error && (
            <div className="bg-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 mb-4 border border-red-500/30">
              {error}
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                maxLength={100}
                className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 border border-gray-600"
                placeholder="Jane Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 border border-gray-600"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 border border-gray-600"
                placeholder="Min 8 characters"
              />
            </div>

            <div className="pt-2 border-t border-gray-700">
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Organisation Name
              </label>
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
                maxLength={100}
                className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 border border-gray-600"
                placeholder="Acme Drilling Pty Ltd"
              />
              <p className="text-gray-500 text-xs mt-1">
                You'll be set as owner and can invite team members from the admin panel.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors mt-2"
            >
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-gray-500 text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-orange-400 hover:text-orange-300 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
